import React, { memo } from 'react';
import { Result, buildTableWithDataId, Grid as GridLib, Row as RowLib, ResultVariants } from '@dexma/ui-components';
import i18n from 'asset/translation';
import { StyledTableWrapper, StyledTitleWrapper } from 'adapter/in/common/components/widgets/Table/style';
import TableData, {
    GridCell,
    Grid,
    OneOfAllSimpleContentMinusTag,
    TableCellType,
    TableColumn,
    Row,
    DateContent,
    TextContent,
    NumericContent,
} from 'domain/entity/widgets/TableData';
import { applyTwoDecimals } from 'adapter/in/common/utils/numberFormatter';
import { DateFormatter } from 'adapter/in/common/components/widgets/utils/dateFormatter';
import {
    OneOfAllPossibleCellTypesViews,
    OneOfAllPossibleSimpleCellTypesViews,
} from 'adapter/in/common/components/widgets/Table/TableWidgetView';
import { LayoutType } from 'domain/entity/layout';
import Branding from 'domain/entity/branding';
import { ColorType, PeriodType, WidgetDescription } from 'domain/entity/widgetData';
import { IconCellComponent, RegularCellComponent, TagCellComponent } from 'adapter/in/common/components/widgets/Table/TableCellComponents';
import { getPeriodFrom, getPeriodTo } from 'adapter/in/common/utils/widgetDataUtils';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';

// Devices (and other tag-based) columns can wrap their text into several lines, so they don't need
// as much reserved horizontal space as columns holding numeric values, which must stay on one line.
const MIN_WRAPPABLE_COLUMN_WIDTHS = {
    [LayoutType.MOBILE]: 40,
    [LayoutType.DESKTOP]: 90,
    [LayoutType.TABLET]: 70,
    [LayoutType.A4]: 70,
};

const parseTitle = (description: WidgetDescription, dateFormat: string) => {
    if (description.period.type === PeriodType.TEXT) {
        return i18n.t('widgets.table.tableTitle', { parameter: description.title, period: description.period.text });
    }
    const from = getPeriodFrom(description.period, dateFormat);
    const to = getPeriodTo(description.period, dateFormat);
    return i18n.t('widgets.table.tableTitleInterval', { parameter: description.title, from, to });
};

type DateTableProps = {
    width: number;
    isLoading: boolean;
    decimalSeparator: string;
    thousandSeparator: string;
    dateFormat: string;
    isCurrencyLastPosition: boolean;
    data: TableData;
    keyAsProp: string;
    layoutType: LayoutType;
    branding: Branding;
    hideOverflowScrollbar?: boolean;
};
const Table = (props: DateTableProps): JSX.Element => {
    const {
        width,
        isLoading,
        data,
        decimalSeparator,
        thousandSeparator,
        dateFormat,
        isCurrencyLastPosition,
        keyAsProp,
        branding,
        layoutType,
        hideOverflowScrollbar,
    } = props;

    const currentMinWrappableColumnWidth = MIN_WRAPPABLE_COLUMN_WIDTHS[layoutType];

    const composeTextToolInstance = composeTextToolWithSettings(isCurrencyLastPosition, decimalSeparator, thousandSeparator, dateFormat);
    const columns = !isLoading
        ? getColumnFormat(data, width, composeTextToolInstance, keyAsProp, currentMinWrappableColumnWidth, branding)
        : [];

    const dataSource = !isLoading ? getDataSource(data) : [];

    const composeTitle = (description: WidgetDescription, df: string): string =>
        description.title !== '' ? parseTitle(description, df) : '';

    const title = isLoading ? '' : composeTitle(data.description, dateFormat);

    const TableWithDataId = buildTableWithDataId<typeof columns>();

    return (
        <StyledTableWrapper data-testid='table-widget' $hideOverflowScrollbar={hideOverflowScrollbar}>
            {title !== '' && <StyledTitleWrapper>{title}</StyledTitleWrapper>}
            <TableWithDataId
                columns={columns as any}
                dataSource={dataSource as any}
                isLoading={isLoading}
                isPaginated={false}
                bordered
                errorContent={
                    <Result
                        variant={ResultVariants.DEFAULT}
                        title={i18n.t('common.errorWrong')}
                        info={i18n.t('common.errorWrongInfo')}
                        icon='bot-dead'
                    />
                }
            />
        </StyledTableWrapper>
    );
};

type ComposeTextTool = (value: number | Date | null, units: string, showSigned?: boolean) => string;

const composeTextTool = (
    currencyLastPosition: boolean,
    decimalSeparator: string,
    thousandSeparator: string,
    dateFormat: string,
    value: number | Date | null,
    units: string,
    showSigned?: boolean
) => {
    if (value === null) {
        return '- ';
    }

    const unitIsCurrencySign = () => {
        const currencySigns = ['$', '€', '£', '¥', '₹']; // Add more currency signs as needed
        return currencySigns.includes(units);
    };

    const provideSignIfRequired = () => {
        if (showSigned && typeof value === 'number' && value > 0) {
            return `+`;
        }
        return '';
    };

    if (value instanceof Date) {
        return new DateFormatter(dateFormat).dhverbose(value);
    }

    if (unitIsCurrencySign() && !currencyLastPosition) {
        return `${units}${provideSignIfRequired()}${applyTwoDecimals(value.toString(), decimalSeparator, thousandSeparator)} `;
    }

    return `${provideSignIfRequired()}${applyTwoDecimals(value.toString(), decimalSeparator, thousandSeparator)} ${units}`;
};

const composeTextToolWithSettings = (
    currencyLastPosition: boolean,
    decimalSeparator: string,
    thousandSeparator: string,
    dateFormat: string
) => (value: number | Date | null, units: string, showSigned = false) =>
    composeTextTool(currencyLastPosition, decimalSeparator, thousandSeparator, dateFormat, value, units, showSigned);

// Widgets/reports are horizontally constrained, so column widths must always add up to exactly the
// available width: columns are never allowed to overflow the widget, forcing the table to fit it.
const TAG_COLUMN_WIDTH_RATIO = 0.65;

const columnHasWrappableContent = (column: TableColumn): boolean =>
    column.cells.some((cell) => cell.content.type === TableCellType.TAG);

const computeColumnWidths = (widgetWidth: number, columns: TableColumn[], minWrappableColumnWidth: number): number[] => {
    const availableWidth = widgetWidth - 26;
    const totalWeight = columns.reduce((sum, column) => sum + column.widthProportion, 0);

    if (totalWeight <= 0 || availableWidth <= 0) {
        return columns.map(() => 0);
    }

    const naiveWidths = columns.map((column) => (availableWidth / totalWeight) * column.widthProportion);

    const wrappableIndexes = columns.reduce<number[]>((indexes, column, index) => {
        if (columnHasWrappableContent(column)) {
            indexes.push(index);
        }
        return indexes;
    }, []);

    if (wrappableIndexes.length === 0 || wrappableIndexes.length === columns.length) {
        return naiveWidths;
    }

    const widths = [...naiveWidths];
    let freedWidth = 0;

    wrappableIndexes.forEach((index) => {
        const shrunkWidth = Math.max(naiveWidths[index] * TAG_COLUMN_WIDTH_RATIO, minWrappableColumnWidth);
        const clampedWidth = Math.min(shrunkWidth, naiveWidths[index]);
        freedWidth += naiveWidths[index] - clampedWidth;
        widths[index] = clampedWidth;
    });

    const nonWrappableWeight = columns.reduce(
        (sum, column, index) => (wrappableIndexes.includes(index) ? sum : sum + column.widthProportion),
        0
    );

    if (nonWrappableWeight > 0) {
        columns.forEach((column, index) => {
            if (!wrappableIndexes.includes(index)) {
                widths[index] += (freedWidth * column.widthProportion) / nonWrappableWeight;
            }
        });
    }

    return widths;
};

type OptionalGridCellProps = {
    xs?: number | null;
    sm?: number | null;
    md?: number | null;
    lg?: number | null;
    xsOffset?: number | null;
    smOffset?: number | null;
    mdOffset?: number | null;
    lgOffset?: number | null;
};
const getColumnFormat = (
    data: TableData,
    widgetWidth: number,
    composeTextToolInstance: ComposeTextTool,
    keyAsProp: string,
    minWrappableColumnWidth: number,
    branding: Branding
) => {
    const columnWidths = computeColumnWidths(widgetWidth, data.columns, minWrappableColumnWidth);

    const getElementByCellType = (content: OneOfAllPossibleCellTypesViews & OptionalGridCellProps, key: string) => {
        const { xs, md, sm, lg, lgOffset, mdOffset, smOffset, xsOffset } = content;

        switch (content.type) {
            case TableCellType.NUMERIC: {
                const numericTableCell = content;
                const { backgroundColor: numericBackgroundColor, fontColor: numericFontColor } = getColors(numericTableCell, branding);
                const value = composeTextToolInstance(numericTableCell.value, numericTableCell.units, numericTableCell.showSignedValue);
                return (
                    <RegularCellComponent
                        key={key}
                        align={numericTableCell.align}
                        backgroundColor={numericBackgroundColor}
                        fontColor={numericFontColor}
                        tooltip={numericTableCell.tooltip}
                        value={value}
                        fontWeight={numericTableCell.fontWeight}
                        xs={xs}
                        md={md}
                        sm={sm}
                        lg={lg}
                        lgOffset={lgOffset}
                        mdOffset={mdOffset}
                        xsOffset={xsOffset}
                        smOffset={smOffset}
                    />
                );
            }
            case TableCellType.DATE: {
                const numericTableCell = content;
                const { backgroundColor: numericBackgroundColor, fontColor: numericFontColor } = getColors(numericTableCell, branding);
                const value = composeTextToolInstance(numericTableCell.value, '', undefined);
                return (
                    <RegularCellComponent
                        key={key}
                        align={numericTableCell.align}
                        backgroundColor={numericBackgroundColor}
                        fontColor={numericFontColor}
                        tooltip={numericTableCell.tooltip}
                        value={value}
                        fontWeight={numericTableCell.fontWeight}
                        xs={xs}
                        md={md}
                        sm={sm}
                        lg={lg}
                        lgOffset={lgOffset}
                        mdOffset={mdOffset}
                        xsOffset={xsOffset}
                        smOffset={smOffset}
                    />
                );
            }
            case TableCellType.ICON: {
                const iconTableCell = content;
                const { iconColor, backgroundColor } = getColors(iconTableCell, branding);
                return (
                    <IconCellComponent
                        value={iconTableCell.value}
                        key={key}
                        align={iconTableCell.align}
                        backgroundColor={backgroundColor}
                        iconColor={iconColor}
                        tooltip={iconTableCell.tooltip}
                        xs={xs}
                        md={md}
                        sm={sm}
                        lg={lg}
                        lgOffset={lgOffset}
                        mdOffset={mdOffset}
                        xsOffset={xsOffset}
                        smOffset={smOffset}
                    />
                );
            }
            case TableCellType.TAG: {
                const tag = content;
                const { backgroundColor: tagBackgroundColor, tagColor: tagTagColor } = getColors(tag, branding);
                const tagValue = getTagValue(tag.content, composeTextToolInstance);

                return (
                    <TagCellComponent
                        key={key}
                        align={tag.align}
                        tagType={tag.tagType}
                        backgroundColor={tagBackgroundColor}
                        tooltip={tag.content.tooltip}
                        tagValue={tagValue}
                        tagBackground={tagTagColor}
                        tagIcon={tag.tagIcon}
                        xs={xs}
                        md={md}
                        sm={sm}
                        lg={lg}
                        lgOffset={lgOffset}
                        mdOffset={mdOffset}
                        xsOffset={xsOffset}
                        smOffset={smOffset}
                    />
                );
            }
            case TableCellType.TEXT: {
                const text = content;
                const { backgroundColor: textBackgroundColor, fontColor: textFontColor } = getColors(text, branding);
                return (
                    <RegularCellComponent
                        key={key}
                        align={text.align}
                        backgroundColor={textBackgroundColor}
                        fontColor={textFontColor}
                        tooltip={text.tooltip}
                        value={text.value}
                        fontWeight={text.fontWeight}
                        xs={xs}
                        md={md}
                        sm={sm}
                        lg={lg}
                        lgOffset={lgOffset}
                        mdOffset={mdOffset}
                        xsOffset={xsOffset}
                        smOffset={smOffset}
                    />
                );
            }
            case TableCellType.GRID: {
                const grid = content;
                return <GridLib key={key}>{getCompositeElement(grid.children, key)} </GridLib>;
            }
            default:
                return <></>;
        }
    };

    type RowComponentProps = {
        children: (Row | GridCell | Grid)[];
        keyProp: string;
    };
    const RowComponent = (props: RowComponentProps) => {
        const { children, keyProp } = props;
        return <RowLib>{getCompositeElement(children, keyProp)}</RowLib>;
    };

    const getCompositeElement = (children: (GridCell | Row | Grid)[], keyProp: string) =>
        children.map((child: GridCell | Row | Grid, index: number) => {
            if ('children' in child) {
                switch (child.type) {
                    case TableCellType.ROW: {
                        const row = child;
                        return (
                            <RowComponent key={`${keyProp}-${index}`} keyProp={`${keyProp}-${index}`}>
                                {row.children}
                            </RowComponent>
                        );
                    }
                    case TableCellType.GRID:
                    default: {
                        const grid = child;
                        return <div key={`${keyProp}-${index}`}>{getCompositeElement(grid.children, `${keyProp}-${index}`)} </div>;
                    }
                }
            }
            const { xs, xsOffset, md, mdOffset, lg, lgOffset, sm, smOffset } = child;
            return getElementByCellType(
                { ...child.content, xs, md, sm, lg, smOffset, mdOffset, lgOffset, xsOffset },
                `${keyProp}-${index}`
            );
        });

    return data.columns.map((column: TableColumn, index: number) => ({
        dataIndex: column.headerTitle,
        key: column.headerTitle,
        title: column.headerTitle,
        width: columnWidths[index],
        fixed: column.fixed,
        className: column.isSortable ? undefined : 'hidden-sort-arrrows',
        showSorterTooltip: !!column.isSortable,
        sorter: (a: Record<number, OneOfAllPossibleCellTypesViews>, b: Record<number, OneOfAllPossibleCellTypesViews>) => {
            if (a[index].isFooter || b[index].isFooter) {
                return 0;
            }
            if (column.isSortable) {
                return a[index].sortValue! - b[index].sortValue!;
            }
            return false;
        },
        sortDirections: ['ascend', 'descend'],
        render: (_: any, record: Record<number, OneOfAllPossibleCellTypesViews>) =>
            getElementByCellType(record[index], `${keyAsProp}-c${index}-r${record[index]}`),
    }));
};

export const getDataSource = (data: TableData) => {
    const mainData = data.columns[0].cells.map((_: unknown, index: number) => {
        const dataSource: Record<number, OneOfAllPossibleCellTypesViews> = {};

        data.columns.forEach((column: TableColumn, columnIndex: number) => {
            const cellContent = {
                ...column.cells[index].content,
                sortValue: column.cells[index].sortValue,
            };

            dataSource[columnIndex] = cellContent;
        });

        return dataSource;
    });

    const footers: Record<number, OneOfAllPossibleCellTypesViews> = data.columns.reduce(
        (result: Record<number, OneOfAllPossibleCellTypesViews>, column: TableColumn, columnIndex: number) => {
            if (column.footer) {
                return {
                    ...result,
                    [columnIndex]: {
                        ...column.footer.content,
                        isFooter: true,
                    },
                };
            }
            return result;
        },
        {}
    );

    return Object.keys(footers).length !== 0 ? [...mainData, footers] : mainData;
};

const getColors = (content: OneOfAllPossibleSimpleCellTypesViews, branding: Branding) => {
    const colors = { backgroundColor: '', fontColor: '', tagColor: '', iconColor: '' };

    const getColorWithBranding = (colorType: ColorType, colorDefault: string) => returnProperColor(colorType, colorDefault, branding);

    if (content.type === TableCellType.TAG && content.tagBackgroundColorDefault && content.tagBackgroundColorType) {
        colors.tagColor = getColorWithBranding(content.tagBackgroundColorType, content.tagBackgroundColorDefault);
    } else if (content.type === TableCellType.ICON && content.iconColorType && content.iconColorDefault) {
        colors.iconColor = getColorWithBranding(content.iconColorType, content.iconColorDefault);
    }
    if (content.backgroundColorType && content.backgroundColorDefault) {
        colors.backgroundColor = getColorWithBranding(content.backgroundColorType, content.backgroundColorDefault);
    }
    if (isRecordWithTextColor(content) && content.fontColorDefault && content.fontColorType) {
        colors.fontColor = getColorWithBranding(content.fontColorType, content.fontColorDefault);
    }

    return colors;
};

const returnProperColor = (type: ColorType, defaultColor: string, branding: Branding) => {
    if (type === ColorType.BY_DEFAULT) return defaultColor;
    if (type === ColorType.PRIMARY) return branding.primaryColor;
    if (type === ColorType.BACKGROUND) return branding.backgroundColor;
    return '';
};

const getTagValue = (tagContent: OneOfAllSimpleContentMinusTag, composeFunction: ComposeTextTool) => {
    if (tagContent.type === TableCellType.DATE) {
        return composeFunction(tagContent.value, '', undefined);
    }
    if (tagContent.type === TableCellType.NUMERIC) {
        return composeFunction(tagContent.value, tagContent.units, undefined);
    }
    return tagContent.value;
};

const isRecordWithTextColor = (content: OneOfAllPossibleSimpleCellTypesViews): content is TextContent | DateContent | NumericContent =>
    content.type === TableCellType.TEXT || content.type === TableCellType.NUMERIC || content.type === TableCellType.DATE;

export default memo(Table, checkMemoChanges);
